/**
 * Human-readable labels for program slot schedules.
 *
 * Slot `times` arrays are Mon-first, 7 elements: MON=0 … SUN=6, with
 * "x" (or empty) meaning no class that day. Used by the reinstatement
 * surfaces (member modal + staff review page); extracted here so both
 * render the identical label.
 */
import { formatTimeTo12Hour } from "./utils";

export const DAY_LABELS_MON_FIRST = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
];

/**
 * "Mon/Wed 4:00 PM" when every active day shares one time, otherwise
 * "Mon 4:00 PM, Thu 5:30 PM". Empty string when nothing is scheduled.
 */
export function formatSlotSchedule(
  times?: (string | null)[] | null
): string {
  if (!Array.isArray(times)) return "";
  const active: { day: string; time: string }[] = [];
  times.forEach((t, i) => {
    if (t && t !== "x") {
      active.push({
        day: DAY_LABELS_MON_FIRST[i] || "",
        time: formatTimeTo12Hour(t),
      });
    }
  });
  if (active.length === 0) return "";
  const uniqueTimes = Array.from(new Set(active.map((a) => a.time)));
  if (uniqueTimes.length === 1) {
    return `${active.map((a) => a.day).join("/")} ${uniqueTimes[0]}`.trim();
  }
  return active.map((a) => `${a.day} ${a.time}`.trim()).join(", ");
}

/**
 * Option label for a slot picker: "Tumbling — #6 · Mon/Wed 4:00 PM".
 * `classNumber` is the display slot number (program_slots.slot_id on
 * enrollable slots, `class_number` on the pending-reinstatements
 * payload) — pass whichever the surface has.
 */
export function slotOptionLabel(
  programLabel: string,
  slot: {
    times?: (string | null)[] | null;
    slot_id?: number | string | null;
    class_number?: number | string | null;
  } | null
): string {
  const classNumber = slot?.class_number ?? slot?.slot_id;
  const schedule = formatSlotSchedule(slot?.times);
  const when = [
    classNumber != null && classNumber !== "" ? `#${classNumber}` : "",
    schedule,
  ]
    .filter(Boolean)
    .join(" · ");
  return when ? `${programLabel} — ${when}` : programLabel;
}
